import React, { useEffect, useRef, useState } from 'react';
import { createRoot, type Root } from 'react-dom/client';
import { CenteredModal, MessageButtonModal } from '@common/modals';
import { PdfViewer } from '../PdfViewer';
import {
  fetchWithLoginRedirect,
  isNavigationRedirectError,
} from '@common/auth/fetchWithLoginRedirect';

const HOST_ID = 'pdf-media-link-popup-root';
const HISTORY_STATE_KEY = 'pdfMediaLinkPopupOpen';
const CHAT_FILE_OPEN_EVENT = 'agora-chat-file-open';
const AUTO_OPEN_PDF_URL_DATASET_KEY = 'autoOpenPdfMediaOverrideUrl';
const AUTO_OPEN_PDF_FILE_NAME_DATASET_KEY = 'autoOpenPdfMediaOverrideFileName';
const AUTO_OPEN_PDF_CLOSE_REDIRECT_URL_DATASET_KEY = 'autoOpenPdfMediaOverrideCloseRedirectUrl';
const EXCLUDED_CONTAINER_SELECTOR = [
  '[data-react-component="FileExplorerBlock"]',
  '[data-react-component="ProbitDocumentsBlock"]',
  '[data-react-component="DocumentsBlock"]',
  '.pdf-media-link-popup',
].join(', ');

type ActivePdf = {
  fileName: string;
  downloadFileName: string;
  objectUrl: string;
  sourceUrl: string;
};

type PendingPdf = {
  fileName: string;
};

type PdfOpenRequest = {
  url: string;
  fileName: string;
  downloadFileName?: string;
  focusElement?: HTMLElement | null;
  closeRedirectUrl?: string | null;
};

type ChatFileOpenPayload = {
  url: string;
  fileName: string;
  contentType?: string;
};

function getFileNameFromUrl(url: URL): string {
  const rawSegment = url.pathname.split('/').filter(Boolean).pop();

  if (!rawSegment) {
    return 'document.pdf';
  }

  try {
    return decodeURIComponent(rawSegment);
  } catch {
    return rawSegment;
  }
}

export function getAnchorLabel(anchor: HTMLAnchorElement, url: URL): string {
  const explicitFileName = anchor.dataset.pdfPopupFileName?.trim();
  if (explicitFileName) {
    return explicitFileName;
  }

  const label = anchor.textContent?.trim();
  return label && label.length > 0 ? label : getFileNameFromUrl(url);
}

export function getAnchorDownloadFileName(anchor: HTMLAnchorElement, url: URL): string {
  return anchor.dataset.pdfPopupDownloadFileName?.trim() || getAnchorLabel(anchor, url);
}

export function isSameOriginPdfMediaLink(anchor: HTMLAnchorElement): URL | null {
  const href = anchor.getAttribute('href');

  if (!href || href.startsWith('#')) {
    return null;
  }

  let url: URL;

  try {
    url = new URL(anchor.href, window.location.href);
  } catch {
    return null;
  }

  const pathname = url.pathname.toLowerCase();

  if (url.origin !== window.location.origin) {
    return null;
  }

  if (!anchor.hasAttribute('data-pdf-popup') && !pathname.startsWith('/media/')) {
    return null;
  }

  if (!anchor.hasAttribute('data-pdf-popup') && !pathname.endsWith('.pdf')) {
    return null;
  }

  return url;
}

function shouldIgnoreAnchor(anchor: HTMLAnchorElement, event: MouseEvent): boolean {
  if (event.defaultPrevented) {
    return true;
  }

  if (event.button !== 0) {
    return true;
  }

  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
    return true;
  }

  if (anchor.hasAttribute('download') || anchor.hasAttribute('data-pdf-popup-ignore')) {
    return true;
  }

  if (anchor.closest(EXCLUDED_CONTAINER_SELECTOR)) {
    return true;
  }

  const protocol = anchor.protocol.toLowerCase();

  if (protocol === 'mailto:' || protocol === 'tel:' || protocol === 'javascript:') {
    return true;
  }

  return false;
}

export async function fetchPdfResponse(url: string): Promise<Response> {
  const parsed = new URL(url, window.location.href);
  if (parsed.origin === window.location.origin && parsed.pathname.startsWith('/api/v3/m-files/')) {
    return fetchWithLoginRedirect<Response>(url, { method: 'GET', expectJson: false });
  }
  return fetch(url, { method: 'GET', credentials: 'include' });
}

function isUnauthorizedResponse(response: Response, blob: Blob): boolean {
  return (
    response.status === 401 ||
    response.status === 403 ||
    (response.redirected && blob.type.toLowerCase().includes('text/html'))
  );
}

function isPdfFile(fileName: string, contentType?: string | null): boolean {
  return (
    contentType?.toLowerCase().split(';', 1)[0].trim() === 'application/pdf' ||
    fileName.toLowerCase().endsWith('.pdf')
  );
}

function getAppVersion(): number | null {
  if (typeof navigator === 'undefined') {
    return null;
  }

  const match = navigator.userAgent.match(/;Agora-app;v(.*?);/);
  return match ? parseInt(match[1], 10) : null;
}

function shouldUseNativeApp(): boolean {
  return !!getAppVersion() && typeof (window as any).ReactNativeWebView !== 'undefined';
}

function normalizeUrlForNative(url: string): string {
  try {
    const asUrl = new URL(url, window.location.origin);
    return asUrl.pathname + asUrl.search + asUrl.hash;
  } catch {
    return url;
  }
}

function triggerNativeFileDownload(rawUrl: string, fileName?: string): boolean {
  if (!shouldUseNativeApp()) {
    return false;
  }

  try {
    (window as any).ReactNativeWebView?.postMessage(
      JSON.stringify({
        event: {
          version: 1,
          type: 'fileDownload',
          data: {
            url: normalizeUrlForNative(rawUrl),
            fileName,
          },
        },
      }),
    );
    return true;
  } catch (err) {
    console.error('Failed to post fileDownload event to native app:', err);
    return false;
  }
}

function downloadViaAnchor(url: string, fileName: string) {
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();
}

function isChatFileOpenPayload(value: unknown): value is ChatFileOpenPayload {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const payload = value as Record<string, unknown>;
  return (
    typeof payload.url === 'string' &&
    payload.url.trim().length > 0 &&
    typeof payload.fileName === 'string' &&
    payload.fileName.trim().length > 0 &&
    (payload.contentType === undefined || typeof payload.contentType === 'string')
  );
}

function getAutoOpenPdfRequest(): PdfOpenRequest | null {
  const bodyDataset = document.body?.dataset;
  const rawUrl = bodyDataset?.[AUTO_OPEN_PDF_URL_DATASET_KEY]?.trim();

  if (!rawUrl) {
    return null;
  }

  try {
    const url = new URL(rawUrl, window.location.href);
    const explicitFileName = bodyDataset?.[AUTO_OPEN_PDF_FILE_NAME_DATASET_KEY]?.trim();
    const closeRedirectUrl = bodyDataset?.[AUTO_OPEN_PDF_CLOSE_REDIRECT_URL_DATASET_KEY]?.trim();

    delete bodyDataset[AUTO_OPEN_PDF_URL_DATASET_KEY];
    delete bodyDataset[AUTO_OPEN_PDF_FILE_NAME_DATASET_KEY];
    delete bodyDataset[AUTO_OPEN_PDF_CLOSE_REDIRECT_URL_DATASET_KEY];

    return {
      url: url.toString(),
      fileName:
        explicitFileName && explicitFileName.length > 0
          ? explicitFileName
          : getFileNameFromUrl(url),
      closeRedirectUrl,
    };
  } catch {
    delete bodyDataset[AUTO_OPEN_PDF_URL_DATASET_KEY];
    delete bodyDataset[AUTO_OPEN_PDF_FILE_NAME_DATASET_KEY];
    delete bodyDataset[AUTO_OPEN_PDF_CLOSE_REDIRECT_URL_DATASET_KEY];
    return null;
  }
}

function hasInternalReferrer(): boolean {
  const referrer = document.referrer?.trim();
  if (!referrer) {
    return false;
  }

  try {
    return new URL(referrer).origin === window.location.origin;
  } catch {
    return false;
  }
}

function navigateAwayFromAutoOpenedPdf(closeRedirectUrl: string) {
  if (hasInternalReferrer() && window.history.length > 2) {
    if (window.history.state?.[HISTORY_STATE_KEY]) {
      window.history.go(-2);
    } else {
      window.history.back();
    }
    return;
  }

  window.location.assign(closeRedirectUrl);
}

function PdfMediaLinkPopup() {
  const [activePdf, setActivePdf] = useState<ActivePdf | null>(null);
  const [pendingPdf, setPendingPdf] = useState<PendingPdf | null>(null);
  const [showUnauthorizedModal, setShowUnauthorizedModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);

  const activePdfRef = useRef<ActivePdf | null>(null);
  const pendingPdfRef = useRef<PendingPdf | null>(null);
  const hasPushedViewerHistoryRef = useRef(false);
  const isNavigatingBackForViewerRef = useRef(false);
  const isLoadingRef = useRef(false);
  const lastFocusedElementRef = useRef<HTMLElement | null>(null);
  const activeCloseRedirectUrlRef = useRef<string | null>(null);

  function revokeActivePdfObjectUrl(pdf: ActivePdf | null) {
    if (pdf?.objectUrl) {
      URL.revokeObjectURL(pdf.objectUrl);
    }
  }

  function closeViewer() {
    const closeRedirectUrl = activeCloseRedirectUrlRef.current;
    if (closeRedirectUrl) {
      navigateAwayFromAutoOpenedPdf(closeRedirectUrl);
      return;
    }

    if (hasPushedViewerHistoryRef.current && !isNavigatingBackForViewerRef.current) {
      isNavigatingBackForViewerRef.current = true;
      window.history.back();
      return;
    }

    hasPushedViewerHistoryRef.current = false;
    isNavigatingBackForViewerRef.current = false;

    setActivePdf((current) => {
      revokeActivePdfObjectUrl(current);
      return null;
    });
    setPendingPdf(null);
    activeCloseRedirectUrlRef.current = null;

    lastFocusedElementRef.current?.focus();
    lastFocusedElementRef.current = null;
  }

  useEffect(() => {
    activePdfRef.current = activePdf;
  }, [activePdf]);

  useEffect(() => {
    pendingPdfRef.current = pendingPdf;
  }, [pendingPdf]);

  useEffect(() => {
    if (activePdf || pendingPdf) {
      document.body.classList.add('modal-open');
      return () => document.body.classList.remove('modal-open');
    }

    document.body.classList.remove('modal-open');
  }, [activePdf, pendingPdf]);

  useEffect(() => {
    function handlePopState() {
      const closeRedirectUrl = activeCloseRedirectUrlRef.current;
      if (closeRedirectUrl) {
        activeCloseRedirectUrlRef.current = null;
        navigateAwayFromAutoOpenedPdf(closeRedirectUrl);
        return;
      }

      if (!hasPushedViewerHistoryRef.current || (!activePdfRef.current && !pendingPdfRef.current)) {
        return;
      }

      hasPushedViewerHistoryRef.current = false;
      isNavigatingBackForViewerRef.current = false;

      setActivePdf((current) => {
        revokeActivePdfObjectUrl(current);
        return null;
      });
      setPendingPdf(null);
      activeCloseRedirectUrlRef.current = null;

      lastFocusedElementRef.current?.focus();
      lastFocusedElementRef.current = null;
    }

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  useEffect(() => {
    if ((!activePdf && !pendingPdf) || hasPushedViewerHistoryRef.current) {
      return;
    }

    if (activeCloseRedirectUrlRef.current) {
      return;
    }

    const currentHistoryState =
      window.history.state && typeof window.history.state === 'object' ? window.history.state : {};

    window.history.pushState(
      {
        ...currentHistoryState,
        [HISTORY_STATE_KEY]: true,
      },
      document.title,
    );

    hasPushedViewerHistoryRef.current = true;
  }, [activePdf, pendingPdf]);

  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key !== 'Escape' || (!activePdfRef.current && !pendingPdfRef.current)) {
        return;
      }

      event.preventDefault();
      closeViewer();
    }

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  useEffect(() => {
    async function openPdf(request: PdfOpenRequest) {
      if (isLoadingRef.current) {
        return;
      }

      isLoadingRef.current = true;
      lastFocusedElementRef.current = request.focusElement ?? null;
      setPendingPdf({
        fileName: request.fileName,
      });

      try {
        const response = await fetchPdfResponse(request.url);

        if (response.status === 401 || response.status === 403) {
          setPendingPdf(null);
          setShowUnauthorizedModal(true);
          return;
        }

        if (!response.ok) {
          setPendingPdf(null);
          setShowErrorModal(true);
          return;
        }

        const blob = await response.blob();

        if (isUnauthorizedResponse(response, blob)) {
          setPendingPdf(null);
          setShowUnauthorizedModal(true);
          return;
        }

        if (blob.size === 0 || blob.type.toLowerCase().includes('text/html')) {
          setPendingPdf(null);
          setShowErrorModal(true);
          return;
        }

        const objectUrl = URL.createObjectURL(blob);

        setActivePdf((current) => {
          revokeActivePdfObjectUrl(current);
          return {
            fileName: request.fileName,
            downloadFileName: request.downloadFileName ?? request.fileName,
            objectUrl,
            sourceUrl: request.url,
          };
        });
        activeCloseRedirectUrlRef.current = request.closeRedirectUrl ?? null;
        if (request.closeRedirectUrl) {
          hasPushedViewerHistoryRef.current = false;
          isNavigatingBackForViewerRef.current = false;
        }
        setPendingPdf(null);
      } catch (error) {
        if (isNavigationRedirectError(error)) return;
        console.error('Error opening PDF media link:', error);
        setPendingPdf(null);
        activeCloseRedirectUrlRef.current = null;
        setShowErrorModal(true);
      } finally {
        isLoadingRef.current = false;
      }
    }

    function handleChatFileOpen(event: Event) {
      if (!(event instanceof CustomEvent) || !isChatFileOpenPayload(event.detail)) {
        return;
      }

      const { url, fileName, contentType } = event.detail;
      if (!isPdfFile(fileName, contentType)) {
        if (!triggerNativeFileDownload(url, fileName)) {
          downloadViaAnchor(url, fileName);
        }
        return;
      }

      void openPdf({
        url,
        fileName,
      });
    }

    function handleDocumentClick(event: MouseEvent) {
      const target = event.target as HTMLElement | null;
      const anchor = target?.closest('a');

      if (!anchor) {
        return;
      }

      if (shouldIgnoreAnchor(anchor, event)) {
        return;
      }

      const url = isSameOriginPdfMediaLink(anchor);

      if (!url) {
        return;
      }

      event.preventDefault();
      void openPdf({
        url: url.toString(),
        fileName: getAnchorLabel(anchor, url),
        downloadFileName: getAnchorDownloadFileName(anchor, url),
        focusElement: anchor,
      });
    }

    document.addEventListener('click', handleDocumentClick);
    window.addEventListener(CHAT_FILE_OPEN_EVENT, handleChatFileOpen);

    const autoOpenRequest = getAutoOpenPdfRequest();
    if (autoOpenRequest) {
      void openPdf(autoOpenRequest);
    }

    return () => {
      document.removeEventListener('click', handleDocumentClick);
      window.removeEventListener(CHAT_FILE_OPEN_EVENT, handleChatFileOpen);
    };
  }, []);

  useEffect(() => {
    return () => {
      activeCloseRedirectUrlRef.current = null;
      revokeActivePdfObjectUrl(activePdfRef.current);
    };
  }, []);

  function handleUnauthorizedModalClose() {
    setShowUnauthorizedModal(false);
  }

  function handleUnauthorizedModalButtonClick() {
    window.location.reload();
  }

  function handleErrorModalClose() {
    setShowErrorModal(false);
  }

  async function handleActivePdfDownload() {
    if (!activePdf) {
      return;
    }

    if (triggerNativeFileDownload(activePdf.sourceUrl, activePdf.downloadFileName)) {
      return;
    }

    try {
      const response = await fetchPdfResponse(activePdf.sourceUrl);

      if (!response.ok) {
        setShowErrorModal(true);
        return;
      }

      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      try {
        downloadViaAnchor(objectUrl, activePdf.downloadFileName);
      } finally {
        window.setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
      }
    } catch (error) {
      if (isNavigationRedirectError(error)) return;
      console.error('Error downloading PDF media link:', error);
      setShowErrorModal(true);
    }
  }

  return (
    <>
      {(activePdf || pendingPdf) && (
        <div className={'pdf-media-link-popup'}>
          <CenteredModal showModal={true} handleCloseModal={closeViewer}>
            <div className={'pdf-media-link-popup__header'}>
              <span className={'pdf-media-link-popup__title'}>
                {activePdf?.fileName ?? pendingPdf?.fileName}
              </span>
              {activePdf && (
                <button
                  type={'button'}
                  className={'pdf-media-link-popup__download'}
                  onClick={() => void handleActivePdfDownload()}
                  aria-label={'Prenesi PDF'}
                  title={'Prenesi PDF'}
                >
                  <i className={'fas fa-download'} aria-hidden={'true'} />
                </button>
              )}
            </div>
            {activePdf ? (
              <PdfViewer
                url={activePdf.objectUrl}
                maxWidth={1100}
                handleException={() => {
                  closeViewer();
                  setShowErrorModal(true);
                }}
              />
            ) : (
              <div className={'pdf-media-link-popup__loading'}>
                <i className="fas fa-spinner fa-spin fa-2x pdf-loading-spinner" />
              </div>
            )}
          </CenteredModal>
        </div>
      )}

      <MessageButtonModal
        showModal={showUnauthorizedModal}
        handleCloseModal={handleUnauthorizedModalClose}
        handleButtonClick={handleUnauthorizedModalButtonClick}
        message={'Seja je potekla'}
        buttonText={'Prijava'}
      />

      <MessageButtonModal
        showModal={showErrorModal}
        handleCloseModal={handleErrorModalClose}
        handleButtonClick={handleErrorModalClose}
        message={'Datoteke trenutno ni mogoče odpreti.'}
        buttonText={'Zapri'}
      />
    </>
  );
}

let popupRoot: Root | null = null;

export function initPdfMediaLinkPopup() {
  if (document.body.id === 'umbracoMainPageBody') {
    return;
  }

  let host = document.getElementById(HOST_ID);

  if (!host) {
    host = document.createElement('div');
    host.id = HOST_ID;
    document.body.appendChild(host);
  }

  if (!popupRoot) {
    popupRoot = createRoot(host);
    popupRoot.render(<PdfMediaLinkPopup />);
  }
}

export function initPdfMediaLinkPopupOnPageLoad() {
  window.addEventListener('DOMContentLoaded', initPdfMediaLinkPopup);
}
